import { useState, useEffect } from "react";
import axios from "axios";
import { X, Plus, Save, Settings, Users, Clock, Trash2, Loader2, DollarSign } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

import { API } from "./apiConfig";

export function SettingsPanel({ isOpen, onClose, settings, onSave }) {
  const [tipEmployees, setTipEmployees] = useState([]);
  const [fixedEmployees, setFixedEmployees] = useState([]);
  const [fixedTipDays, setFixedTipDays] = useState({});
  const [militaryTime, setMilitaryTime] = useState(true);
  const [timezone, setTimezone] = useState("America/Los_Angeles");
  const [newTipName, setNewTipName] = useState("");
  const [saving, setSaving] = useState(false);
  const [newFixed, setNewFixed] = useState({ name: "", hours: "", tips: false, days: "" });
  const [tipSearch, setTipSearch] = useState("");

  useEffect(() => {
    if (settings) {
      setTipEmployees(settings.tip_employees || []);
      setFixedEmployees(settings.fixed_employees || []);
      setFixedTipDays(settings.fixed_tip_days || {});
      setMilitaryTime(settings.military_time !== false);
      setTimezone(settings.timezone || "America/Los_Angeles");
    }
  }, [settings]);

  // Tip employees stored as array of names that ARE tipped.
  // Toggle: if in list = tipped, remove from list = not tipped.
  const toggleTip = (name) => {
    setTipEmployees((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name].sort()
    );
  };

  const addTipEmployee = () => {
    const name = newTipName.trim();
    if (!name || tipEmployees.includes(name)) return;
    setTipEmployees((prev) => [...prev, name].sort());
    setNewTipName("");
  };

  const addFixedEmployee = () => {
    const name = newFixed.name.trim();
    if (!name) return;
    const entry = {
      name,
      hours: newFixed.hours ? parseFloat(newFixed.hours) : null,
      regular: newFixed.hours ? parseFloat(newFixed.hours) : null,
      tips: newFixed.tips,
      days: newFixed.days ? parseInt(newFixed.days) : null,
    };
    setFixedEmployees((prev) => [...prev, entry]);
    setNewFixed({ name: "", hours: "", tips: false, days: "" });
  };

  const removeFixedEmployee = (index) => setFixedEmployees((prev) => prev.filter((_, i) => i !== index));

  const updateFixedEmployee = (index, field, value) => {
    setFixedEmployees((prev) => prev.map((fe, i) => {
      if (i !== index) return fe;
      const updated = { ...fe, [field]: value };
      // Keep regular in sync with hours (they're the same for fixed employees)
      if (field === "hours") updated.regular = value;
      return updated;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { tip_employees: tipEmployees, fixed_employees: fixedEmployees, fixed_tip_days: fixedTipDays, military_time: militaryTime, timezone };
    try {
      await axios.post(`${API}/settings`, payload, { withCredentials: true });
      onSave(payload);
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Failed to save", { description: err.response?.data?.detail || "Error" });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // All known employees = tip employees list (populated after CSV processing)
  const allEmployees = [...tipEmployees].sort();
  const filteredEmployees = tipSearch
    ? allEmployees.filter((n) => n.toLowerCase().includes(tipSearch.toLowerCase()))
    : allEmployees;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="settings-overlay">
      <div className="absolute inset-0 bg-slate-50/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-slate-50 border-l border-slate-200 h-full flex flex-col animate-in slide-in-from-right duration-200" data-testid="settings-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 text-[#0A1F3B]">
            <Settings size={18} />
            <h2 className="font-heading text-lg font-bold">Settings</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-[#0A1F3B] transition-colors" data-testid="settings-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Time Format */}
          <div data-testid="military-time-section">
            <div className="flex items-center gap-2 text-[#0A1F3B] font-semibold text-sm mb-3">
              <Clock size={14} /> Time Format
            </div>
            <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-600">
              <div className={`relative w-9 h-5 rounded-full transition-colors ${militaryTime ? "bg-[#0A1F3B]" : "bg-slate-200"}`}>
                <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-transform ${militaryTime ? "left-[18px]" : "left-0.5"}`} />
              </div>
              <input type="checkbox" checked={militaryTime} onChange={(e) => setMilitaryTime(e.target.checked)} className="sr-only" data-testid="military-time-toggle" />
              {militaryTime ? "24-hour (Military)" : "12-hour (AM/PM)"}
            </label>
          </div>

          {/* Timezone */}
          <div data-testid="timezone-section">
            <div className="flex items-center gap-2 text-[#0A1F3B] font-semibold text-sm mb-3">
              <Clock size={14} /> Timezone
            </div>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full h-8 text-xs bg-white border border-slate-200 rounded-md px-2 text-[#0A1F3B] focus:border-[#0A1F3B] focus:outline-none"
              data-testid="timezone-select"
            >
              <option value="America/New_York">Eastern (ET)</option>
              <option value="America/Chicago">Central (CT)</option>
              <option value="America/Denver">Mountain (MT)</option>
              <option value="America/Los_Angeles">Pacific (PT)</option>
              <option value="America/Anchorage">Alaska (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii (HT)</option>
            </select>
          </div>

          {/* Tip Employees - Checkbox List */}
          <div data-testid="tip-employees-section">
            <div className="flex items-center gap-2 text-[#0A1F3B] font-semibold text-sm mb-1">
              <DollarSign size={14} /> Tip Employees
            </div>
            <p className="text-[10px] text-slate-500 mb-3">
              {allEmployees.length === 0
                ? "No employees yet. Process a payroll CSV to populate this list."
                : "Uncheck employees who should not receive tips (e.g. part-time)."}
            </p>

            {allEmployees.length > 0 && (
              <>
                {allEmployees.length > 6 && (
                  <Input
                    placeholder="Search employees..."
                    value={tipSearch}
                    onChange={(e) => setTipSearch(e.target.value)}
                    className="h-7 text-xs bg-white border-slate-200 text-[#0A1F3B] placeholder-slate-400 mb-2"
                    data-testid="tip-search-input"
                  />
                )}
                <div className="space-y-0.5 max-h-[240px] overflow-y-auto mb-3">
                  {filteredEmployees.map((name) => (
                    <label
                      key={name}
                      className="flex items-center gap-2.5 px-3 py-1.5 rounded hover:bg-white cursor-pointer transition-colors"
                      data-testid={`tip-toggle-${name.replace(/\s/g, '-')}`}
                    >
                      <input
                        type="checkbox"
                        checked={tipEmployees.includes(name)}
                        onChange={() => toggleTip(name)}
                        className="w-3.5 h-3.5 accent-[#0A1F3B] cursor-pointer"
                      />
                      <span className="text-xs text-[#0A1F3B]">{name}</span>
                      {tipEmployees.includes(name) && (
                        <span className="ml-auto text-[9px] text-[#0A1F3B]/60 font-semibold">TIPS</span>
                      )}
                    </label>
                  ))}
                </div>
              </>
            )}

            {allEmployees.length === 0 && (
              <div className="py-6 text-center bg-white border border-dashed border-slate-200 rounded-lg">
                <Users size={24} className="text-slate-500 mx-auto mb-2" />
                <p className="text-xs text-slate-500">
                  Process a CSV to see employees here
                </p>
              </div>
            )}

            {/* Manual add for edge cases */}
            <div className="flex gap-2 mt-2">
              <Input
                data-testid="new-tip-employee-input"
                placeholder="Add employee manually..."
                value={newTipName}
                onChange={(e) => setNewTipName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTipEmployee()}
                className="flex-1 h-7 text-xs bg-white border-slate-200 text-[#0A1F3B] placeholder-slate-400"
              />
              <Button size="sm" onClick={addTipEmployee} className="bg-slate-200 text-[#0A1F3B] hover:bg-[#2A5080] h-7 text-xs" data-testid="add-tip-employee-btn">
                <Plus size={12} />
              </Button>
            </div>
          </div>

          {/* Fixed Employees */}
          <div data-testid="fixed-employees-section">
            <div className="flex items-center gap-2 text-[#0A1F3B] font-semibold text-sm mb-1">
              <Users size={14} /> Fixed Hour Employees ({fixedEmployees.length})
            </div>
            <p className="text-[10px] text-slate-500 mb-3">Employees not in Clover POS, added to totals with fixed hours.</p>
            <div className="space-y-1.5 mb-3">
              {fixedEmployees.map((fe, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-white rounded text-xs flex-wrap" data-testid={`fixed-emp-row-${fe.name.replace(/\s/g, '-')}`}>
                  <span className="font-semibold text-[#0A1F3B] min-w-[60px]">{fe.name}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[9px] text-slate-500 uppercase">Hours</span>
                    <input
                      type="number"
                      step="0.01"
                      value={fe.hours ?? ""}
                      placeholder="-"
                      onChange={(e) => updateFixedEmployee(i, "hours", e.target.value === "" ? null : parseFloat(e.target.value))}
                      className="w-14 h-5 text-[10px] font-mono text-[#0A1F3B] bg-slate-50 border border-slate-200 rounded px-1 text-center focus:border-[#0A1F3B] focus:outline-none"
                      data-testid={`fixed-hours-${fe.name.replace(/\s/g, '-')}`}
                    />
                  </div>
                  <label className="flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={fe.tips || false} onChange={(e) => updateFixedEmployee(i, "tips", e.target.checked)} className="w-3 h-3 accent-[#0A1F3B]" /> Tips
                  </label>
                  {fe.tips && (
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-slate-500 uppercase">Days</span>
                      <input
                        type="number"
                        value={fe.days ?? ""}
                        placeholder="-"
                        onChange={(e) => updateFixedEmployee(i, "days", e.target.value === "" ? null : parseInt(e.target.value))}
                        className="w-10 h-5 text-[10px] font-mono text-[#0A1F3B] bg-slate-50 border border-slate-200 rounded px-1 text-center focus:border-[#0A1F3B] focus:outline-none"
                        data-testid={`fixed-days-${fe.name.replace(/\s/g, '-')}`}
                      />
                    </div>
                  )}
                  <button onClick={() => removeFixedEmployee(i)} className="ml-auto text-slate-500 hover:text-red-700 transition-colors">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input placeholder="Name" value={newFixed.name} onChange={(e) => setNewFixed({ ...newFixed, name: e.target.value })} className="flex-1 min-w-[80px] h-7 text-xs bg-white border-slate-200 text-[#0A1F3B] placeholder-slate-400" />
              <Input placeholder="Hrs" type="number" value={newFixed.hours} onChange={(e) => setNewFixed({ ...newFixed, hours: e.target.value })} className="w-14 h-7 text-xs bg-white border-slate-200 text-[#0A1F3B] placeholder-slate-400" />
              <Button size="sm" onClick={addFixedEmployee} className="bg-slate-200 text-[#0A1F3B] hover:bg-[#2A5080] h-7 text-xs" data-testid="add-fixed-employee-btn">
                <Plus size={12} />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end px-5 py-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0A1F3B] text-white font-bold hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(10,31,59,0.3)] transition-all" data-testid="save-settings-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            <span className="ml-1">Save</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
